import { useEffect, useRef, useState } from 'react';
import parse from 'html-react-parser';
import { Modal } from 'react-bootstrap';
import moment from 'moment-timezone';

import { Company } from '../../classess/company/company';
import { Country } from '../../classess/country/country';
import { CompanyService } from '../../services/companies/CompanyService';

import {
    badgetStatusGeneric, getProcessButtons, checkUrl, setDateTimeFormatGlobalString, handleResponseMessageAsync, isUndNullEmp, isValidEmail, isValidPhoneNumber,
    isValidUrl, getLanguageObj, getResponse, settings, styles
} from '../../helpers/main';
import { GlobalFormats } from '../../classess/main/globalFormats';

const currentMethod = 'CompanyAddEditDetails';

const { icons, images, labels, messages, } = settings;

export default function CompanyAddEditDetails({
    auth = {},
    modalObj = {
        $: {},
        type: 0,
        auth: auth,
        showModal: false,
        countries: [Country?.class({}, false)],
        languageObj: GlobalFormats?.getClass(getLanguageObj({ subMethod: currentMethod })?.result),
        dtCompaniesId: '',
        dtCompaniesRowId: 0,
    },
    setModalObj = {},
    company = Company?.getClass({}, false),
    setCompany = {},
}) {
    //#region variables
    const [secondaryIdError, setSecondaryIdError] = useState('');
    const [nameError, setNameError] = useState('');
    const [rifError, setRifError] = useState('');
    const [nitError, setNitError] = useState('');
    const [phonesError, setPhonesError] = useState('');
    const [faxError, setFaxError] = useState('');
    const [countryError, setCountryError] = useState('');
    const [addressError, setAddressError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [webSiteError, setWebSiteError] = useState('');
    const [registerDateError, setRegisterDateError] = useState('');
    const [dataFormLength] = useState(Company.minMaxLength(false));
    const [process, setProcess] = useState({
        showLoading: false,
        save: false,
    });
    const isMount = useRef(true);
    const controls = {
        buttons: {
            back: {
                defaultClass: 'btn btn-sm btn-outline-success',
                id: 'btnBack',
            },
            save: {
                defaultClass: 'btn btn-sm btn-outline-primary mx-1',
                id: 'btnSave',
            },
            closeModal: {
                defaultClass: 'close btn-danger fas fa-window-close',
                id: 'btnModalClose',
            },
        },
        checkBox: {
            isAvailable: {
                id: 'chkIsAvailable',
            },
        },
        icons: {
            save: {
                id: 'iconSave',
                defaultClass: icons?.fasFa?.save,
            },
            back: {
                id: 'iconBack',
                defaultClass: icons?.fasFa?.arrowCircleLeft,
            },
        },
        inputs: {
            secondaryId: {
                id: 'inputSecondaryId',
            },
            name: {
                id: 'inputName',
            },
            rif: {
                id: 'inputRif',
            },
            nit: {
                id: 'inputNit',
            },
            phones: {
                id: 'inputPhones',
            },
            fax: {
                id: 'inputFax',
            },
            email: {
                id: 'inputEmail',
            },
            webSite: {
                id: 'inputWebSite',
            },
            registerDate: {
                id: 'inputRegisterDate',
            },
        },
        pickers: {
            registerDate: {
                id: 'pkrRegisterDate',
            },
        },
        selects: {
            country: {
                id: 'selCountry'
            },
        },
        textAreas: {
            address: {
                id: 'textAreaAddress',
            }
        }
    };
    //#endregion variables

    //#region methods
    //#region company
    const addCompany = async ({
        e = undefined,
        subMethod = '',
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'addCompany';
        try {
            if (e) e?.preventDefault?.();

            response = setErrorsDefault({ subMethod: currentMethod });
            if (!response?.isSuccess || response?.isWarning) return;

            response = setCompanyData({ subMethod: currentMethod });
            if (!response?.isSuccess || response?.isWarning) return;

            response = await validateFormData();
            if (!response?.isSuccess || response?.isWarning) return;

            setProcess(prev => ({ ...prev, save: true }));

            response = await CompanyService.create({
                auth: modalObj?.auth,
                data: company,
                languageId: modalObj?.languageObj?.id
            });

            if (!response?.isSuccess || response?.isWarning) return;

            const newCompany = Company?.getClass(response?.result?.company, false);

            //#region update dataTable
            modalObj.$(`#${modalObj.dtCompaniesId}`).DataTable().row.add([
                newCompany?.secundaryId,
                newCompany?.name,
                badgetStatusGeneric({
                    type: newCompany?.isAvailable ? 0 : 5,
                    value: newCompany?.isAvailable ? labels?.yes?.[modalObj?.languageObj?.id] : labels?.no?.[modalObj?.languageObj?.id],
                }),
                setDateTimeFormatGlobalString({
                    date: newCompany?.registerDate,
                    dateFormat: modalObj?.languageObj?.format?.date?.f1,
                    timeFormat: 0,
                    withSeconds: false,
                })?.result,
                getProcessButtons({
                    id: newCompany?.id,
                    description: newCompany?.name,
                    showDetails: true,
                    labelDetails: labels?.details[modalObj?.languageObj?.id],
                    enabledDetails: true,
                    showDelete: true,
                    labelDelete: labels?.delete[modalObj?.languageObj?.id],
                    enabledDelete: true,
                    showEdit: true,
                    labelEdit: labels?.edit?.[modalObj?.languageObj?.id],
                    enabledEdit: true,
                })?.result,
            ])?.draw(false);
            //#endregion update dataTable

            response.msgText = `${messages?.processCompletedSuccessfully?.[modalObj?.languageObj?.id]}`;
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        } finally {
            if (!isMount?.current) return;

            setProcess(prev => ({ ...prev, save: false }));

            if (response?.isSuccess && !response?.isWarning) {
                setDefaultValue({ closeModal: true, subMethod: currentMethod });
            }

            if (!response?.isSuccess || response?.isWarning || response?.msgText !== '' || response?.msgTextHtml !== '') {
                await handleResponseMessageAsync({ response, currentMethod, auth: modalObj?.auth, languageId: modalObj?.languageObj?.id });
            }
        }
    }

    const editCompany = async ({
        e = undefined,
        subMethod = '',
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'editCompany';
        try {
            if (e) e?.preventDefault?.();

            response = setErrorsDefault({ subMethod: currentMethod });
            if (!response?.isSuccess || response?.isWarning) return;

            response = setCompanyData({ subMethod: currentMethod });
            if (!response?.isSuccess || response?.isWarning) return;

            response = await validateFormData();
            if (!response?.isSuccess || response?.isWarning) return;

            setProcess(prev => ({ ...prev, save: true }));

            response = await CompanyService.update({
                auth: modalObj?.auth,
                id: company?.id,
                data: company,
                languageId: modalObj?.languageObj?.id
            });

            if (!response?.isSuccess || response?.isWarning) return;

            const updatedCompany = Company?.getClass(response?.result?.company, false);

            //#region update dataTable
            modalObj.$(`#${modalObj.dtCompaniesId}`).DataTable().row(modalObj.dtCompaniesRowId).data([
                updatedCompany?.secundaryId,
                updatedCompany?.name,
                badgetStatusGeneric({
                    type: updatedCompany?.isAvailable ? 0 : 5,
                    value: updatedCompany?.isAvailable ? labels?.yes?.[modalObj?.languageObj?.id] : labels?.no?.[modalObj?.languageObj?.id],
                }),
                setDateTimeFormatGlobalString({
                    date: updatedCompany?.registerDate,
                    dateFormat: modalObj?.languageObj?.format?.date?.f1,
                    timeFormat: 0,
                    withSeconds: false,
                })?.result,
                getProcessButtons({
                    id: updatedCompany?.id,
                    description: updatedCompany?.name,
                    showDetails: true,
                    labelDetails: labels?.details[modalObj?.languageObj?.id],
                    enabledDetails: true,
                    showDelete: true,
                    labelDelete: labels?.delete[modalObj?.languageObj?.id],
                    enabledDelete: true,
                    showEdit: true,
                    labelEdit: labels?.edit?.[modalObj?.languageObj?.id],
                    enabledEdit: true,
                })?.result,
            ])?.draw(false);
            //#endregion update dataTable

            response.msgText = `${settings?.messages?.processCompletedSuccessfully?.[modalObj?.languageObj?.id]}`;
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        } finally {
            if (!isMount?.current) return;

            setProcess(prev => ({ ...prev, save: false }));

            if (response?.isSuccess && !response?.isWarning) {
                setDefaultValue({ closeModal: true, subMethod: currentMethod });
            }

            if (!response?.isSuccess || response?.isWarning || response?.msgText !== '' || response?.msgTextHtml !== '') {
                await handleResponseMessageAsync({ response, currentMethod, auth: modalObj?.auth, languageId: modalObj?.languageObj?.id });
            }
        }
    }
    //#endregion company

    //#region others
    const getData = async ({
        subMethod = ''
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'getData';
        try {
            response = setDateInControl({
                date: new Date(),
                registerDate: company?.registerDate,
                subMethod: currentMethod,
            });
            if (!response?.isSuccess || response?.isWarning) return;
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        } finally {
            if (!isMount?.current) return;

            if (!response?.isSuccess || response?.isWarning || response?.msgText !== '' || response?.msgTextHtml !== '') {
                await handleResponseMessageAsync({ response, currentMethod, auth: modalObj?.auth, languageId: modalObj?.languageObj?.id });
            }
        }
    }

    const setDateInControl = ({
        date = undefined,
        registerDate = undefined,
        subMethod = '',
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'setDateInControl';
        try {
            const { languageObj, $ } = modalObj;
            let dateTime = '';
            const dateTimeFormat = languageObj?.format?.date?.f11;
            const dateFormat = languageObj?.format?.date?.f1;

            if (registerDate) {
                dateTime = setDateTimeFormatGlobalString({ date: registerDate, dateFormat: dateFormat, timeFormat: 0, withSeconds: false })?.result;
            } else if (date) {
                dateTime = setDateTimeFormatGlobalString({ date: date, dateFormat: dateFormat, timeFormat: 0, withSeconds: false })?.result;
            }

            $.fn.datetimepicker.Constructor.Default = $.extend({}, $.fn.datetimepicker.Constructor.Default, {
                icons: settings?.react?.tempusDominus?.icons,
                buttons: settings?.react?.tempusDominus?.buttons,
                tooltips: settings?.react?.tempusDominus?.tooltips({ languageId: languageObj?.id }),
                locale: languageObj?.id,
            });

            $(`#${controls.pickers.registerDate.id}`)?.datetimepicker('options', {
                date: dateTime,
                default: false,
                timepicker: false,
                format: dateTimeFormat,
            });
            return getResponse({ type: 0 });
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    }

    const setCompanyData = ({
        subMethod = ''
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'setCompanyData';
        try {
            const { languageObj, $ } = modalObj;
            const registerDate = $(`#${controls.pickers.registerDate.id}`)?.datetimepicker('date')?._d;
            company.registerDate = moment(registerDate).format(`${languageObj?.format?.date?.f13}T${languageObj?.format?.time?.f2}-04:00`);
            return getResponse({ type: 0 });
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    }

    const setDefaultValue = ({
        closeModal = false,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'setDefaultValue';
        let response = getResponse({ type: 0 });
        try {
            if (closeModal) {
                setModalObj(prev => ({
                    ...prev,
                    showModal: false,
                    type: 0,
                }));
            }
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
            console.error(response?.msgText);
        }
    }

    const validateFormData = async ({
        subMethod = '',
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'validateFormData';
        try {
            const { languageObj } = modalObj;
            let hasError = false;
            const requiredMsg = `${messages?.thisFieldRequired?.[languageObj?.id]}.`;

            if (isUndNullEmp({ data: company?.secundaryId })?.result) {
                setSecondaryIdError(requiredMsg);
                hasError = true;
            }

            if (isUndNullEmp({ data: company?.name })?.result) {
                setNameError(requiredMsg);
                hasError = true;
            }

            if (isUndNullEmp({ data: company?.rif })?.result) {
                setRifError(requiredMsg);
                hasError = true;
            }

            if (!isUndNullEmp({ data: company?.phones })?.result && !isValidPhoneNumber({ data: company?.phones })) {
                setPhonesError(`${messages?.youMustEnterValidPhoneNumber[languageObj?.id]}.`);
                hasError = true;
            }

            if (!isUndNullEmp({ data: company?.fax })?.result && !isValidPhoneNumber({ data: company?.fax })) {
                setFaxError(`${messages?.youMustEnterValidPhoneNumber[languageObj?.id]}.`);
                hasError = true;
            }

            if (!company?.countryId || company?.countryId <= 0) {
                setCountryError(requiredMsg);
                hasError = true;
            }

            if (isUndNullEmp({ data: company?.address })?.result) {
                setAddressError(requiredMsg);
                hasError = true;
            }

            if (isUndNullEmp({ data: company?.email })?.result) {
                setEmailError(requiredMsg);
                hasError = true;
            } else if (!isValidEmail({ data: company?.email })) {
                setEmailError(`${messages?.youMustEnterValidEmail[languageObj?.id]}.`);
                hasError = true;
            }

            if (isUndNullEmp({ data: company?.webSite })?.result) {
                setWebSiteError(requiredMsg);
                hasError = true;
            } else if (!isValidUrl({ data: company?.webSite })?.result) {
                setWebSiteError(`${messages?.youMustEnterValidUrl[languageObj?.id]}.`);
                hasError = true;
            }

            const resDate = modalObj.$(`#${controls.pickers.registerDate.id}`)?.datetimepicker('date');
            if (!resDate) {
                setRegisterDateError(requiredMsg);
                hasError = true;
            }

            response = getResponse({ type: 0 });
            if (hasError) response.isWarning = true;
            return response;
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    }

    const setErrorsDefault = ({
        subMethod = '',
    } = {}) => {
        const currentMethod = 'setErrorsDefault';
        let response = getResponse({ type: 0 });
        try {
            setSecondaryIdError('');
            setNameError('');
            setRifError('');
            setNitError('');
            setPhonesError('');
            setFaxError('');
            setCountryError('');
            setAddressError('');
            setEmailError('');
            setWebSiteError('');
            setRegisterDateError('');
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        } finally {
            //
        }

        return response;
    }

    useEffect(() => {
        isMount.current = true;
        getData({ subMethod: 'form - onLoad' });
        return () => {
            isMount.current = false;
        }
    }, []);// eslint-disable-line react-hooks/exhaustive-deps
    //#endregion others
    //#endregion methods

    return (
        <Modal backdrop='static' show={modalObj?.showModal} centered size=''>
            <Modal.Header className='border-bottom-0 pt-4 px-4 bg-white'>
                <Modal.Title className='d-flex align-items-center w-100'>
                    <div className='bg-light p-2 rounded-circle mr-3 shadow-sm'
                        style={{ width: '45px', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img alt='company' src={images?.icons?.company} style={{ ...styles?.cardHeaderLogo }} />
                    </div>
                    <div className='d-flex flex-column'>
                        <span className='h5 mb-0 font-weight-bold text-dark'>{labels?.company?.[modalObj?.languageObj?.id]}</span>
                        <div className='d-flex align-items-center mt-1'>
                            <span className={`badge badge-pill py-1 px-2 text-uppercase ${modalObj?.type === 0 ? 'badge-info' : modalObj?.type === 1 ? 'badge-warning text-dark' : 'badge-secondary'}`} style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>
                                {modalObj?.type === 0 ? labels?.add?.[modalObj?.languageObj?.id] :
                                    modalObj?.type === 1 ? labels?.edit?.[modalObj?.languageObj?.id] :
                                        labels?.detail?.[modalObj?.languageObj?.id]}
                            </span>
                        </div>
                    </div>
                </Modal.Title>
                <button id={controls?.buttons?.closeModal?.id}
                    type='button'
                    className='close border-0 bg-transparent'
                    data-dismiss='modal'
                    aria-label='Close'
                    disabled={process?.save}
                    style={{ fontSize: '1.5rem', marginTop: '-15px' }}
                    onClick={() => setDefaultValue({ closeModal: true, subMethod: '', })}>
                    <span aria-hidden='true'>&times;</span>
                </button>
            </Modal.Header>
            <form onSubmit={(e) => modalObj?.type === 0 ? addCompany({ e: e, subMethod: 'form' }) : modalObj?.type === 1 ? editCompany({ e: e, subMethod: 'form' }) : e?.preventDefault()}>
                <Modal.Body className='p-0'>
                    <div className='card-body p-4'>
                        {modalObj?.type !== 2 ? (
                            <div className='row'>
                                {/* Code / Identificador */}
                                <div className='col-md-4 mb-3'>
                                    <div className='form-group'>
                                        <small className={settings?.html?.labels?.mutedUpperFontWeightBoldDblockMb2}>
                                            <i className={icons?.indentFasFa} /> {labels?.code?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <input autoFocus disabled={process?.save}
                                            id={controls?.inputs?.secondaryId.id}
                                            className='form-control bg-light border-0 shadow-sm'
                                            style={{ borderRadius: '5px' }}
                                            maxLength={dataFormLength?.max?.secundaryId}
                                            onChange={(e) => setCompany(prev => ({ ...prev, secundaryId: e?.target?.value }))}
                                            onFocus={() => setSecondaryIdError('')}
                                            value={company?.secundaryId || ''}
                                            placeholder={labels?.code?.[modalObj?.languageObj?.id]}
                                            title={labels?.code?.[modalObj?.languageObj?.id]} />
                                        <div className='form-text text-danger' style={styles?.inputMsgAlert}>{parse(secondaryIdError)}</div>
                                    </div>
                                </div>

                                {/* Name / Description */}
                                <div className='col-md-8 mb-3'>
                                    <div className='form-group'>
                                        <small className={settings?.html?.labels?.mutedUpperFontWeightBoldDblockMb2}>
                                            <i className={icons?.fasFa?.spellCheck} /> {labels?.description?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <input disabled={process?.save}
                                            id={controls?.inputs?.name?.id}
                                            className='form-control bg-light border-0 shadow-sm'
                                            style={{ borderRadius: '5px' }}
                                            maxLength={dataFormLength?.max?.name}
                                            onChange={(e) => setCompany(prev => ({ ...prev, name: e?.target?.value }))}
                                            onFocus={() => setNameError('')}
                                            value={company?.name || ''}
                                            placeholder={labels?.description?.[modalObj?.languageObj?.id]}
                                            title={labels?.description?.[modalObj?.languageObj?.id]} />
                                        <div className='form-text text-danger' style={styles?.inputMsgAlert}>{parse(nameError)}</div>
                                    </div>
                                </div>

                                {/* RIF */}
                                <div className='col-md-6 mb-3'>
                                    <div className='form-group'>
                                        <small className={settings?.html?.labels?.mutedUpperFontWeightBoldDblockMb2}>
                                            <i className={icons?.fasFa?.spellCheck} /> {labels?.rif?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <input disabled={process?.save}
                                            id={controls?.inputs?.rif.id}
                                            className='form-control bg-light border-0 shadow-sm'
                                            style={{ borderRadius: '5px' }}
                                            maxLength={dataFormLength?.max?.rif}
                                            onChange={(e) => setCompany(prev => ({ ...prev, rif: e?.target?.value }))}
                                            onFocus={() => setRifError('')}
                                            value={company?.rif || ''}
                                            placeholder={labels?.rif?.[modalObj?.languageObj?.id]}
                                            title={labels?.rif?.[modalObj?.languageObj?.id]} />
                                        <div className='form-text text-danger' style={styles?.inputMsgAlert}>{parse(rifError)}</div>
                                    </div>
                                </div>

                                {/* NIT */}
                                <div className='col-md-6 mb-3'>
                                    <div className='form-group'>
                                        <small className={settings?.html?.labels?.mutedUpperFontWeightBoldDblockMb2}>
                                            <i className={icons?.fasFa?.spellCheck} /> {labels?.nit?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <input disabled={process?.save}
                                            id={controls?.inputs?.nit.id}
                                            className='form-control bg-light border-0 shadow-sm'
                                            style={{ borderRadius: '5px' }}
                                            maxLength={dataFormLength?.max?.nit}
                                            onChange={(e) => setCompany(prev => ({ ...prev, nit: e?.target?.value }))}
                                            onFocus={() => setNitError('')}
                                            value={company?.nit || ''}
                                            placeholder={labels?.nit?.[modalObj?.languageObj?.id]}
                                            title={labels?.nit?.[modalObj?.languageObj?.id]} />
                                        <div className='form-text text-danger' style={styles?.inputMsgAlert}>{parse(nitError)}</div>
                                    </div>
                                </div>

                                {/* Phones */}
                                <div className='col-md-6 mb-3'>
                                    <div className='form-group'>
                                        <small className={settings?.html?.labels?.mutedUpperFontWeightBoldDblockMb2}>
                                            <i className={icons?.mobileAlt} /> {labels?.phones?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <input disabled={process?.save}
                                            id={controls?.inputs?.phones?.id}
                                            className='form-control bg-light border-0 shadow-sm'
                                            style={{ borderRadius: '5px' }}
                                            maxLength={dataFormLength?.max?.phones}
                                            onChange={(e) => setCompany(prev => ({ ...prev, phones: e?.target?.value }))}
                                            onFocus={() => setPhonesError('')}
                                            value={company?.phones || ''}
                                            placeholder={labels?.phones?.[modalObj?.languageObj?.id]}
                                            title={labels?.phones?.[modalObj?.languageObj?.id]} />
                                        <div className='form-text text-danger' style={styles?.inputMsgAlert}>{parse(phonesError)}</div>
                                    </div>
                                </div>

                                {/* Fax */}
                                <div className='col-md-6 mb-3'>
                                    <div className='form-group'>
                                        <small className={settings?.html?.labels?.mutedUpperFontWeightBoldDblockMb2}>
                                            <i className={icons?.fax} /> {labels?.fax?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <input disabled={process?.save}
                                            id={controls?.inputs?.fax.id}
                                            className='form-control bg-light border-0 shadow-sm'
                                            style={{ borderRadius: '5px' }}
                                            maxLength={dataFormLength?.max?.fax}
                                            onChange={(e) => setCompany(prev => ({ ...prev, fax: e?.target?.value }))}
                                            onFocus={() => setFaxError('')}
                                            value={company?.fax || ''}
                                            placeholder={labels?.fax?.[modalObj?.languageObj?.id]}
                                            title={labels?.fax?.[modalObj?.languageObj?.id]} />
                                        <div className='form-text text-danger' style={styles?.inputMsgAlert}>{parse(faxError)}</div>
                                    </div>
                                </div>

                                {/* Email */}
                                <div className='col-md-6 mb-3'>
                                    <div className='form-group'>
                                        <small className={settings?.html?.labels?.mutedUpperFontWeightBoldDblockMb2}>
                                            <i className={icons?.envelope} /> {labels?.email?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <input disabled={process?.save}
                                            id={controls?.inputs?.email?.id}
                                            className='form-control bg-light border-0 shadow-sm'
                                            style={{ borderRadius: '5px' }}
                                            maxLength={dataFormLength?.max?.email}
                                            onChange={(e) => setCompany(prev => ({ ...prev, email: e?.target?.value }))}
                                            onFocus={() => setEmailError('')}
                                            value={company?.email || ''}
                                            placeholder={labels?.email?.[modalObj?.languageObj?.id]}
                                            title={labels?.email?.[modalObj?.languageObj?.id]} />
                                        <div className='form-text text-danger' style={styles?.inputMsgAlert}>{parse(emailError)}</div>
                                    </div>
                                </div>

                                {/* WebSite */}
                                <div className='col-md-6 mb-3'>
                                    <div className='form-group'>
                                        <small className={settings?.html?.labels?.mutedUpperFontWeightBoldDblockMb2}>
                                            <i className={icons?.globe} /> {labels?.webSite?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <input disabled={process?.save}
                                            id={controls?.inputs?.webSite.id}
                                            className='form-control bg-light border-0 shadow-sm'
                                            style={{ borderRadius: '5px' }}
                                            maxLength={dataFormLength?.max?.webSite}
                                            onChange={(e) => setCompany(prev => ({ ...prev, webSite: e?.target?.value }))}
                                            onFocus={() => setWebSiteError('')}
                                            value={company?.webSite || ''}
                                            placeholder={labels?.webSite?.[modalObj?.languageObj?.id]}
                                            title={labels?.webSite?.[modalObj?.languageObj?.id]} />
                                        <div className='form-text text-danger' style={styles?.inputMsgAlert}>{parse(webSiteError)}</div>
                                    </div>
                                </div>

                                {/* Country */}
                                <div className='col-md-6 mb-3'>
                                    <div className='form-group'>
                                        <small className={settings?.html?.labels?.mutedUpperFontWeightBoldDblockMb2}>
                                            <i className={icons?.globe} /> {labels?.country?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <select disabled={process?.save}
                                            id={controls?.selects?.country.id}
                                            className='custom-select bg-light border-0 shadow-sm'
                                            style={{ borderRadius: '5px' }}
                                            value={company?.countryId || 0}
                                            onChange={(e) => setCompany(prev => ({ ...prev, countryId: parseInt(e?.target?.value), }))}
                                            onFocus={() => setCountryError('')}
                                            title={labels?.country?.[modalObj?.languageObj?.id]}>
                                            <option key={`0`} value={`0`}>{messages?.selectCountry?.[modalObj?.languageObj?.id]}</option>
                                            {modalObj.countries.map((country) => <option key={country.id} value={country.id}>{country.name}</option>)}
                                        </select>
                                        <div className='form-text text-danger' style={styles?.inputMsgAlert}>{parse(countryError)}</div>
                                    </div>
                                </div>

                                {/* Register Date (Picker) */}
                                <div className='col-md-6 mb-3'>
                                    <div className='form-group'>
                                        <small className={settings?.html?.labels?.mutedUpperFontWeightBoldDblockMb2}>
                                            <i className={icons?.faFa?.calendarAlt} /> {labels?.registered?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <div id={controls.pickers.registerDate.id} className='input-group date' data-target-input='nearest'>
                                            <input disabled={process?.save}
                                                id={controls?.inputs?.registerDate.id}
                                                className='form-control bg-light border-0 shadow-sm datetimepicker-input'
                                                style={{ borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px' }}
                                                data-target={`#${controls.pickers.registerDate.id}`}
                                                data-toggle='datetimepicker'
                                                onFocus={() => setRegisterDateError('')}
                                                placeholder={labels?.date?.[modalObj?.languageObj?.id]}
                                                title={labels?.date?.[modalObj?.languageObj?.id]}
                                                type='text' />
                                            <div className='input-group-append' data-target={`#${controls.pickers.registerDate.id}`} data-toggle='datetimepicker'>
                                                <div className='input-group-text bg-secondary border-0 shadow-sm' style={{ borderTopRightRadius: '10px', borderBottomRightRadius: '10px' }}>
                                                    <i className={icons?.faFa?.calendarAlt} />
                                                </div>
                                            </div>
                                        </div>
                                        <div className='form-text text-danger' style={styles?.inputMsgAlert}>{parse(registerDateError)}</div>
                                    </div>
                                </div>

                                {/* Address (TextArea) */}
                                <div className='col-md-12 mb-3'>
                                    <div className='form-group'>
                                        <small className={settings?.html?.labels?.mutedUpperFontWeightBoldDblockMb2}>
                                            <i className={icons?.mapMarkedAlt} /> {labels?.address?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <textarea disabled={process?.save}
                                            id={controls.textAreas.address.id}
                                            className='form-control bg-light border-0 shadow-sm'
                                            style={{ minHeight: '100px', borderRadius: '10px' }}
                                            maxLength={dataFormLength?.max?.address}
                                            onChange={(e) => setCompany(prev => ({ ...prev, address: e?.target?.value }))}
                                            onFocus={() => setAddressError('')}
                                            placeholder={labels?.address?.[modalObj?.languageObj?.id]}
                                            rows={3}
                                            title={labels?.address?.[modalObj?.languageObj?.id]}
                                            value={company?.address || ''}>
                                        </textarea>
                                        <div className='form-text text-danger' style={styles?.labelMsgAlert}>{parse(addressError)}</div>
                                    </div>
                                </div>

                                {/* Is Available (Checkbox Toggle) */}
                                <div className='col-md-12 mb-0'>
                                    <div className='form-group'>
                                        <input disabled={process?.save} id={controls?.checkBox?.isAvailable.id}
                                            type='checkbox'
                                            style={{ cursor: (process?.save || modalObj?.type === 2) ? 'default' : 'pointer' }}
                                            data-toggle='toggle'
                                            data-size='xs'
                                            data-width={50}
                                            data-onstyle='primary'
                                            data-on={labels?.yes?.[modalObj?.languageObj?.id]}
                                            data-off={labels?.no?.[modalObj?.languageObj?.id]}
                                            onChange={(e) => setCompany({ ...company, isAvailable: e?.target?.checked })}
                                            defaultChecked={company?.isAvailable || false} />
                                        {' '}<label className='control-label font-weight-bold text-muted text-uppercase ml-2' style={{ fontSize: '0.8rem', cursor: (process?.save || modalObj?.type === 2) ? 'default' : 'pointer' }} htmlFor={controls?.checkBox?.isAvailable.id}>
                                            {labels?.isActive?.[modalObj?.languageObj?.id]}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className='row'>
                                {/* Details View */}
                                <div className='col-md-12 mb-4'>
                                    <div className='p-3 rounded bg-light border-left border-primary shadow-sm h-100'>
                                        <small className='text-muted text-uppercase font-weight-bold d-block mb-1' style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                                            <i className={icons?.fasFa?.spellCheck} /> {labels?.description?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <div className='h5 mb-0 font-weight-bold text-dark' style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{company?.name || '---'}</div>
                                    </div>
                                </div>

                                <div className='col-md-6 mb-3'>
                                    <div className='p-3 rounded bg-light border-left border-info shadow-sm h-100'>
                                        <small className='text-muted text-uppercase font-weight-bold d-block mb-1' style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                                            <i className={icons?.indentFasFa} /> {labels?.code?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <div className='h6 mb-0 font-weight-bold' style={{ wordBreak: 'break-word' }}>{company?.secundaryId || '---'}</div>
                                    </div>
                                </div>

                                <div className='col-md-6 mb-3'>
                                    <div className='p-3 rounded bg-light border-left border-secondary shadow-sm h-100'>
                                        <small className='text-muted text-uppercase font-weight-bold d-block mb-1' style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                                            <i className={icons?.fasFa?.spellCheck} /> {labels?.rif?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <div className='h6 mb-0 font-weight-bold' style={{ wordBreak: 'break-word' }}>{company?.rif || '---'}</div>
                                    </div>
                                </div>

                                <div className='col-md-6 mb-3'>
                                    <div className='p-3 rounded bg-light border-left border-secondary shadow-sm h-100'>
                                        <small className='text-muted text-uppercase font-weight-bold d-block mb-1' style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                                            <i className={icons?.fasFa?.spellCheck} /> {labels?.nit?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <div className='h6 mb-0 font-weight-bold' style={{ wordBreak: 'break-word' }}>{company?.nit || '---'}</div>
                                    </div>
                                </div>

                                <div className='col-md-6 mb-3'>
                                    <div className='p-3 rounded bg-light border-left border-success shadow-sm h-100'>
                                        <small className='text-muted text-uppercase font-weight-bold d-block mb-1' style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                                            <i className={icons?.mobileAlt} /> {labels?.phones?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <div className='h6 mb-0 font-weight-bold' style={{ wordBreak: 'break-word' }}>{company?.phones || '---'}</div>
                                    </div>
                                </div>

                                <div className='col-md-6 mb-3'>
                                    <div className='p-3 rounded bg-light border-left border-success shadow-sm h-100'>
                                        <small className='text-muted text-uppercase font-weight-bold d-block mb-1' style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                                            <i className={icons?.fax} /> {labels?.fax?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <div className='h6 mb-0 font-weight-bold' style={{ wordBreak: 'break-word' }}>{company?.fax || '---'}</div>
                                    </div>
                                </div>

                                <div className='col-md-6 mb-3'>
                                    <div className='p-3 rounded bg-light border-left border-primary shadow-sm h-100'>
                                        <small className='text-muted text-uppercase font-weight-bold d-block mb-1' style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                                            <i className={icons?.envelope} /> {labels?.email?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <div className='h6 mb-0 font-weight-bold' style={{ wordBreak: 'break-word' }}>
                                            {company?.email ? <a href={`mailto:${company?.email}`} className='text-primary'>{company?.email}</a> : '---'}
                                        </div>
                                    </div>
                                </div>

                                <div className='col-md-6 mb-3'>
                                    <div className='p-3 rounded bg-light border-left border-info shadow-sm h-100'>
                                        <small className='text-muted text-uppercase font-weight-bold d-block mb-1' style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                                            <i className={icons?.globe} /> {labels?.webSite?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <div className='h6 mb-0 font-weight-bold' style={{ wordBreak: 'break-word' }}>
                                            {company?.webSite ? <a href={checkUrl({ data: company?.webSite, subMethod: '' })?.result} target='_blank' rel='noreferrer' className='text-primary'>{company?.webSite}</a> : '---'}
                                        </div>
                                    </div>
                                </div>

                                <div className='col-md-6 mb-3'>
                                    <div className='p-3 rounded bg-light border-left border-secondary shadow-sm h-100'>
                                        <small className='text-muted text-uppercase font-weight-bold d-block mb-1' style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                                            <i className={icons?.globe} /> {labels?.country?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <div className='h6 mb-0 font-weight-bold'>
                                            {modalObj.countries.find(({ id }) => id === company?.countryId)?.name || '---'}
                                        </div>
                                    </div>
                                </div>

                                <div className='col-md-6 mb-3'>
                                    <div className='p-3 rounded bg-light border-left border-secondary shadow-sm h-100'>
                                        <small className='text-muted text-uppercase font-weight-bold d-block mb-1' style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                                            <i className={icons?.faFa?.calendarAlt} /> {labels?.registered?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <div className='h6 mb-0 font-weight-bold'>
                                            {company?.registerDate ? setDateTimeFormatGlobalString({
                                                date: company?.registerDate,
                                                dateFormat: modalObj?.languageObj?.format?.date?.f1,
                                                timeFormat: 0,
                                                withSeconds: false,
                                                languageId: modalObj?.languageObj?.id,
                                                subMethod: ''
                                            })?.result : '---'}
                                        </div>
                                    </div>
                                </div>

                                <div className='col-md-6 mb-3'>
                                    <div className='p-3 rounded bg-light border-left border-primary shadow-sm h-100'>
                                        <small className='text-muted text-uppercase font-weight-bold d-block mb-1' style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                                            <i className={icons?.fasFa?.list} /> {labels?.enabled?.[modalObj?.languageObj?.id]}
                                        </small>
                                        <div className='h6 mb-0'>
                                            {parse(badgetStatusGeneric({
                                                type: company?.isAvailable ? 0 : 5,
                                                value: company?.isAvailable ? labels?.yes?.[modalObj?.languageObj?.id] : labels?.no?.[modalObj?.languageObj?.id]
                                            }))}
                                        </div>
                                    </div>
                                </div>

                                <div className='col-md-12 mt-2'>
                                    <small className='text-muted text-uppercase font-weight-bold d-block mb-2' style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                                        <i className={icons?.mapMarkedAlt} /> {labels?.address?.[modalObj?.languageObj?.id]}
                                    </small>
                                    <div className='p-3 rounded bg-white border shadow-sm' style={{ minHeight: '100px', fontSize: '0.95rem', lineHeight: '1.6', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                        {company?.address || '---'}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer className='border-top-0 pb-4 px-4 bg-white'>
                    <div className='d-flex justify-content-center w-100'>
                        <button id={controls?.buttons?.save?.id}
                            className='btn btn-primary shadow-sm font-weight-bold mx-1 px-4'
                            style={{ borderRadius: '5px' }}
                            disabled={process?.save}
                            hidden={modalObj?.type === 2}
                            title={modalObj?.type === 0 ? labels?.add?.[modalObj?.languageObj?.id] : labels?.save?.[modalObj?.languageObj?.id]}
                            type='submit'>
                            {process?.save ? (
                                <span className={settings?.react?.html?.spinner?.borderSm} role='status' aria-hidden='true'></span>
                            ) : (
                                <i id={controls?.icons?.save?.id} className={`${controls?.icons?.save?.defaultClass} mr-2`} />
                            )}
                            {modalObj?.type === 0 ? labels?.add?.[modalObj?.languageObj?.id] : labels?.save?.[modalObj?.languageObj?.id]}
                        </button>
                        <button id={controls?.buttons?.back?.id}
                            className='btn btn-outline-secondary shadow-sm font-weight-bold mx-1 px-4'
                            style={{ borderRadius: '5px' }}
                            disabled={process?.save}
                            title={labels?.close?.[modalObj?.languageObj?.id]}
                            type='button'
                            onClick={() => setDefaultValue({ closeModal: true, subMethod: controls?.buttons?.back?.id })}>
                            <i id={controls?.icons?.back?.id} className={`${controls?.icons?.back?.defaultClass} mr-2`}></i> {labels?.back?.[modalObj?.languageObj?.id]}
                        </button>
                    </div>
                </Modal.Footer>
            </form>
        </Modal >
    )
}